/**
 * La página pública de propuestas habla con la base solo a través de aquí,
 * con service_role: nunca ve la tabla, solo nombre de pila, fecha y lugar.
 * Cada visita importante se convierte en un aviso push al móvil de Juan.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { enviarAviso } from "./avisosPush.server";
import { createServerAdminClient } from "./supabase.server";

export interface PropuestaPublica {
  nombre: string;
  nombrePareja: string | null;
  fechaBoda: string | null;
  lugar: string | null;
  /** URL absoluta de la imagen de la vista previa de WhatsApp, si se generó. */
  imagenVistaPrevia: string | null;
}

const token = z.string().regex(/^[a-f0-9]{32}$/);

export const leerPropuesta = createServerFn({ method: "GET" })
  .validator((input: unknown) => z.object({ token }).parse(input))
  .handler(async ({ data }): Promise<PropuestaPublica | null> => {
    const admin = createServerAdminClient();
    const { data: fila, error } = await admin.rpc("crm_propuesta_publica", { p_token: data.token });
    if (error) throw new Error(error.message);
    if (!fila) return null;
    const propuesta = fila as {
      nombre: string;
      nombre_pareja: string | null;
      fecha_boda: string | null;
      lugar: string | null;
      og_image_path: string | null;
    };
    return {
      nombre: propuesta.nombre,
      nombrePareja: propuesta.nombre_pareja,
      fechaBoda: propuesta.fecha_boda,
      lugar: propuesta.lugar,
      imagenVistaPrevia: propuesta.og_image_path
        ? admin.storage.from("propuestas").getPublicUrl(propuesta.og_image_path).data.publicUrl
        : null,
    };
  });

const visitaSchema = z.object({
  token,
  evento: z.enum(["ABIERTA", "NOMBRE", "VIDEO_INICIO", "VIDEO_COMPLETO", "FINAL"]),
  detalle: z.string().trim().max(80).optional(),
});

/** Apunta lo que hace la pareja y, si toca, avisa a Juan. Nunca falla hacia la pareja. */
export const registrarVisita = createServerFn({ method: "POST" })
  .validator((input: unknown) => visitaSchema.parse(input))
  .handler(async ({ data }): Promise<{ ok: boolean }> => {
    try {
      const admin = createServerAdminClient();
      const { data: resultado, error } = await admin.rpc("crm_propuesta_registrar", {
        p_token: data.token,
        p_evento: data.evento,
        p_detalle: data.detalle ?? null,
      });
      if (error) throw new Error(error.message);
      const visita = resultado as { lead_id: string; aviso: string | null } | null;
      // Sin etiqueta: cada aviso queda aparte en el móvil, ninguno sustituye al anterior.
      if (visita?.aviso) {
        await enviarAviso(admin, {
          titulo: "Propuesta",
          texto: visita.aviso,
          url: `/app/leads?lead=${visita.lead_id}`,
        });
      }
      return { ok: true };
    } catch (fallo) {
      console.error("Visita de propuesta sin registrar", fallo);
      return { ok: false };
    }
  });
